import { getDailyRecapReportAction } from "./dailyRecapReportLink";

export interface NotificationDetailRow {
  key: string;
  label: string;
  value: string;
}

const TECHNICAL_KEYS = new Set([
  "url",
  "notification_category",
  "created_by_user_id",
  "actor_user_id",
  "actor_type",
  "production_log_id",
  "adjustment_id",
  "order_id",
  "product_id",
  "store_id",
  "warehouse_id",
  "from_warehouse_id",
  "to_warehouse_id",
  "recap_date",
  "currency",
  "has_activity",
  "activity_profiles",
  "purchase_line_count",
  "production_output_line_count",
  "expense_id",
  "task_id",
]);

const LABELS: Record<string, string> = {
  task_title: "Tâche",
  actor_name: "Effectué par",
  production_code: "Référence de production",
  production_unit_name: "Produit fabriqué",
  order_number: "Numéro du document",
  customer_name: "Client",
  supplier_name: "Fournisseur",
  total_amount: "Montant total",
  items_count: "Nombre d’articles",
  product_name: "Produit",
  adjustment_type: "Type de mouvement",
  quantity: "Quantité",
  current_stock: "Stock après ajustement",
  stock_threshold: "Seuil de stock",
  unit_name: "Unité",
  unit_short_name: "Unité",
  transfer_number: "Référence du transfert",
  from_warehouse_name: "Établissement d’origine",
  to_warehouse_name: "Établissement de destination",
  total_items: "Produits concernés",
  total_quantity: "Quantité totale",
  company_name: "Entreprise",
  warehouse_name: "Établissement",
  lookback_days: "Période analysée",
  positive_adjustment_count: "Ajustements à la hausse",
  distinct_adjustment_days: "Jours avec ajustement",
  distinct_product_count: "Produits concernés",
  purchase_count: "Achats enregistrés",
  production_count: "Productions enregistrées",
  manual_inbound_share_percent: "Part des entrées manuelles",
  expense_reference: "Référence de la dépense",
  expense_category: "Catégorie",
  expense_amount_display: "Montant",
  expense_missing_supplier_count: "Dépenses sans fournisseur",
  expense_missing_supplier_amount_display: "Montant concerné",
  purchase_missing_supplier_count: "Achats sans fournisseur",
  purchase_missing_supplier_amount_display: "Montant concerné",
  old_expense_count: "Anciennes dépenses à payer",
  old_expense_amount_display: "Restant à payer",
  overdue_expense_count: "Dépenses après échéance",
  overdue_expense_amount_display: "Solde après échéance",
  old_purchase_count: "Anciens achats à payer",
  old_purchase_amount_display: "Restant à payer",
  expense_age_days: "Seuil appliqué aux dépenses",
  purchase_age_days: "Seuil appliqué aux achats",
  old_sales_count: "Factures de vente à encaisser",
  old_sales_amount_display: "Solde à encaisser",
  receivables_customer_count: "Clients concernés",
  oldest_sale_age_days: "Ancienneté de la plus ancienne",
  sales_age_days: "Seuil appliqué aux ventes",
  due_today_count: "Dépenses à échéance aujourd’hui",
  due_today_amount_display: "Solde à régler",
  due_today_missing_supplier_count: "Sans fournisseur",
  due_date: "Date d’échéance",
  invoice_count: "Factures à régler",
  overdue_invoice_count: "Factures en retard",
  outstanding_amount_display: "Encours de souscription",
  overdue_amount_display: "Montant en retard",
  oldest_overdue_days: "Retard maximal",
  credit_limit_display: "Plafond autorisé",
  days_before_suspension: "Jours avant suspension",
  access_state: "État d’accès calculé",
};

const TYPE_FIELDS: Record<string, string[]> = {
  production_completed: [
    "actor_name",
    "production_code",
    "production_unit_name",
    "outputs",
  ],
  sale_completed: [
    "actor_name",
    "order_number",
    "customer_name",
    "total_amount",
    "items_count",
  ],
  purchase_received: [
    "actor_name",
    "order_number",
    "supplier_name",
    "total_amount",
    "items_count",
  ],
  stock_adjustment: [
    "actor_name",
    "product_name",
    "adjustment_type",
    "quantity",
    "unit_short_name",
    "current_stock",
  ],
  stock_alert: [
    "product_name",
    "current_stock",
    "stock_threshold",
    "unit_short_name",
  ],
  transfer_received: [
    "actor_name",
    "transfer_number",
    "from_warehouse_name",
    "to_warehouse_name",
    "total_items",
    "total_quantity",
  ],
  stock_supply_practice_alert: [
    "lookback_days",
    "positive_adjustment_count",
    "distinct_adjustment_days",
    "distinct_product_count",
    "purchase_count",
    "production_count",
    "manual_inbound_share_percent",
  ],
  expense_supplier_missing: [
    "expense_reference",
    "expense_category",
    "expense_amount_display",
  ],
  payables_regularization_alert: [
    "expense_missing_supplier_count",
    "expense_missing_supplier_amount_display",
    "purchase_missing_supplier_count",
    "purchase_missing_supplier_amount_display",
    "old_expense_count",
    "old_expense_amount_display",
    "overdue_expense_count",
    "overdue_expense_amount_display",
    "old_purchase_count",
    "old_purchase_amount_display",
    "expense_age_days",
    "purchase_age_days",
  ],
  expense_due_alert: [
    "due_date",
    "due_today_count",
    "due_today_amount_display",
    "due_today_missing_supplier_count",
  ],
  sales_receivables_alert: [
    "old_sales_count",
    "old_sales_amount_display",
    "receivables_customer_count",
    "oldest_sale_age_days",
    "sales_age_days",
  ],
  subscription_billing_alert: [
    "due_date",
    "invoice_count",
    "overdue_invoice_count",
    "outstanding_amount_display",
    "overdue_amount_display",
    "oldest_overdue_days",
    "credit_limit_display",
    "days_before_suspension",
    "access_state",
  ],
};

const formatNumber = (value: unknown) =>
  (Number(value) || 0).toLocaleString("fr-FR");

function humanizeKey(key: string) {
  return key
    .replace(/_/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function formatValue(key: string, value: unknown): string {
  if (key === "due_date") {
    const [year, month, day] = String(value).slice(0, 10).split("-");
    return year && month && day ? `${day}/${month}/${year}` : String(value);
  }
  if (key === "total_amount") return `${formatNumber(value)} FCFA`;
  if (key === "lookback_days") return `${formatNumber(value)} jours`;
  if (key === "oldest_overdue_days" || key === "days_before_suspension") {
    return `${formatNumber(value)} jours`;
  }
  if (key === "manual_inbound_share_percent") return `${formatNumber(value)} %`;
  if (
    [
      "expense_age_days",
      "purchase_age_days",
      "oldest_sale_age_days",
      "sales_age_days",
    ].includes(key)
  ) {
    return `${formatNumber(value)} jours`;
  }
  if (
    [
      "quantity",
      "current_stock",
      "stock_threshold",
      "items_count",
      "total_items",
      "total_quantity",
    ].includes(key)
  ) {
    return formatNumber(value);
  }
  if (key === "adjustment_type") {
    return value === "add" ? "Entrée en stock" : "Sortie de stock";
  }
  if (key === "outputs" && Array.isArray(value)) {
    return value
      .map((output) => {
        const item = output as Record<string, unknown>;
        const quantity = Number(item.quantity) || 0;
        const unit =
          item.unit_short_name ||
          item.unit_name ||
          (Math.abs(quantity) === 1 ? "unité" : "unités");
        return `${formatNumber(quantity)} ${unit} de ${item.product_name || "produit"}`;
      })
      .join(", ");
  }
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  return String(value);
}

export function getNotificationDetailRows(
  type: string,
  data: Record<string, unknown>
): NotificationDetailRow[] {
  const preferredFields = TYPE_FIELDS[type];
  const keys = preferredFields || Object.keys(data);

  return keys.flatMap((key) => {
    const value = data[key];
    if (
      TECHNICAL_KEYS.has(key) ||
      value === null ||
      value === undefined ||
      value === "" ||
      (typeof value === "object" && key !== "outputs") ||
      (key === "outputs" && (!Array.isArray(value) || value.length === 0))
    ) {
      return [];
    }

    return [{
      key,
      label: key === "outputs" ? "Produits ajoutés au stock" : LABELS[key] || humanizeKey(key),
      value: formatValue(key, value),
    }];
  });
}

export function getNotificationAction(
  type: string,
  data: Record<string, unknown>,
  fallbackWarehouseId: unknown = null
) {
  if (type === "daily_recap") {
    return getDailyRecapReportAction(data, fallbackWarehouseId);
  }

  const defaultUrls: Record<string, string> = {
    stock_alert: "/gestion-stock",
    transfer_received: "/gestion-stock",
  };
  const url =
    typeof data.url === "string" && data.url.startsWith("/")
      ? data.url
      : defaultUrls[type] || null;
  if (!url) return null;

  const labels: Record<string, string> = {
    production_completed: "Voir les productions",
    sale_completed: "Voir les ventes",
    purchase_received: "Voir les achats",
    stock_adjustment: "Voir le stock",
    stock_alert: "Voir le stock",
    transfer_received: "Voir le transfert à réceptionner",
    stock_supply_practice_alert: "Ouvrir le module conseillé",
    expense_supplier_missing: "Compléter la dépense",
    expense_due_alert: "Voir les dépenses à régler",
    payables_regularization_alert: "Régulariser les pièces",
    sales_receivables_alert: "Enregistrer les encaissements",
    subscription_billing_alert: "Voir les factures de souscription",
    planning_event: "Ouvrir la tâche",
  };

  return { url, label: labels[type] || "Ouvrir la page associée" };
}
